import { SpinnerIcon } from './SpinnerIcon';
import { type AnchorHTMLAttributes, type ButtonHTMLAttributes, type Ref } from 'react';
import { Link, type LinkProps } from 'react-router-dom';
import { cn } from '../../lib/utils';

type ButtonVariant = 'primary' | 'secondary' | 'danger' | 'ghost';
type ButtonSize = 'sm' | 'md' | 'lg';
type ButtonShape = 'rounded' | 'pill';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  shape?: ButtonShape;
  loading?: boolean;
  success?: boolean;
  successAnnouncement?: string;
}

export const SUCCESS_HOLD_MS = 1500;
export const INLINE_TEXT_HOLD_MS = 2500;

const variantStyles: Record<ButtonVariant, string> = {
  primary: 'bg-primary text-white hover:bg-primary-dark active:bg-primary-dark',
  secondary: 'bg-bg-muted text-secondary hover:bg-border active:bg-border',
  danger: 'bg-error text-white hover:bg-error-dark active:bg-error-dark',
  ghost: 'bg-transparent text-secondary hover:bg-bg-muted active:bg-border',
};

const sizeStyles: Record<ButtonSize, string> = {
  sm: 'px-3 py-1.5 text-sm',
  md: 'px-4 py-2 text-sm',
  lg: 'px-6 py-3 text-base',
};

const shapeStyles: Record<ButtonShape, string> = {
  rounded: 'rounded-lg',
  pill: 'rounded-full',
};

/**
 * A button's look, for an element that must be something other than a `<button>`. A link that
 * looks like a button is `ButtonLink`; reach for this only when neither fits.
 */
export function buttonClassName({ variant = 'primary', size = 'md', shape = 'rounded' }: { variant?: ButtonVariant; size?: ButtonSize; shape?: ButtonShape } = {}): string {
  return cn(
    'relative inline-flex items-center justify-center gap-2 overflow-hidden font-medium transition-colors',
    'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary',
    shapeStyles[shape],
    variantStyles[variant],
    sizeStyles[size],
  );
}

export function Button({ ref, className, variant = 'primary', size = 'md', shape = 'rounded', loading, success, successAnnouncement = 'Saved', disabled, children, ...props }: ButtonProps & { ref?: Ref<HTMLButtonElement> }) {
    return (
      <button
        ref={ref}
        className={cn(
          buttonClassName({ variant, size, shape }),
          !success && 'disabled:pointer-events-none disabled:opacity-50',
          className,
        )}
        aria-busy={Boolean(loading)}
        disabled={disabled || loading || success}
        {...props}
      >
        {/* Children always rendered to preserve natural width - hidden during loading/success via opacity.
            Delay the fade-in so an enclosing chip strip / row collapse can finish before the
            original label re-appears (otherwise the user sees "tick -> Save -> close"). */}
        <span
          className={cn(
            'inline-flex items-center gap-2 transition-opacity',
            (loading || success)
              ? 'opacity-0 duration-150'
              : 'opacity-100 duration-150 delay-200',
          )}
        >
          {children}
        </span>

        {/* Loading overlay - centered spinner, no width impact */}
        <span
          aria-hidden={!loading}
          className={cn(
            'absolute inset-0 flex items-center justify-center transition-opacity duration-200',
            loading ? 'opacity-100' : 'opacity-0 pointer-events-none',
          )}
        >
          <SpinnerIcon className="h-4 w-4" />
        </span>

        {/* Success overlay - animated tick. Subtle 4px slide-up + fade so the tick visibly
            arrives (without overlapping the spinner) and the polyline draw carries on from there. */}
        <span
          aria-hidden={!success}
          className={cn(
            'absolute inset-0 flex items-center justify-center transition-all duration-200',
            success ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-1 pointer-events-none',
          )}
        >
          <svg
            className="h-4 w-4 stroke-current"
            viewBox="0 0 24 24"
            fill="none"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            {/* Mount only when success flips true so the keyframe replays each time */}
            {success && <polyline points="20 6 9 17 4 12" className="animate-draw-check" />}
          </svg>
        </span>

        {/* Toggle text content (not the node) so SR clients announce the change */}
        <span className="sr-only" role="status" aria-live="polite" aria-atomic="true">
          {success ? successAnnouncement : ''}
        </span>
      </button>
    );
}

interface ButtonLinkOwnProps {
  variant?: ButtonVariant;
  size?: ButtonSize;
  shape?: ButtonShape;
  /** Layout utilities only (`w-full sm:w-auto`, `flex-shrink-0`); the look comes from the variant props. */
  className?: string;
}

type ButtonLinkToProps = ButtonLinkOwnProps & { href?: never } & Omit<LinkProps, 'className'> & { ref?: Ref<HTMLAnchorElement> };

type ButtonLinkHrefProps = ButtonLinkOwnProps & { to?: never } & Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'className' | 'href'> & {
    href: string;
    ref?: Ref<HTMLAnchorElement>;
  };

export type ButtonLinkProps = ButtonLinkToProps | ButtonLinkHrefProps;

/**
 * Navigation that sits among buttons and should look like one: `to` renders a react-router `Link`,
 * `href` a plain `<a>`. Never wrap a `Button` in a `Link` instead - that nests a button in an anchor,
 * which is invalid and gives keyboard users two tab stops for one action. A link cannot be disabled, so
 * a call site that needs a disabled state renders a disabled `Button` in its place.
 */
export function ButtonLink({ variant, size, shape, className, ...props }: ButtonLinkProps) {
  const classes = cn('no-underline', buttonClassName({ variant, size, shape }), className);

  if ('to' in props && props.to !== undefined) {
    const { to, ref, ...rest } = props as ButtonLinkToProps;
    return <Link to={to} ref={ref} className={classes} {...rest} />;
  }

  const { href, ref, ...rest } = props as ButtonLinkHrefProps;
  return <a href={href} ref={ref} className={classes} {...rest} />;
}
